import { Box, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import useFetch from '../../../../api/useFetch';
import usePersistedState from '../../../../global/usePersistedState';
import ExoProcessing from '../../ExoProcessing';
import { ExoSkeleton } from '../../ExoSkeleton';
import ProjectTaskPhase from './ProjectTaskPhase';
import TaskFilterBar from './TaskFilterBar';
import { emptyTaskFilter, visiblePhases } from './taskFilterQuery';
import { applyTaskStatsDelta } from './overview/overviewStats';
import ProjectOverviewStats from './overview/ProjectOverviewStats';

/**
 * Task oriented view of a project: phases > work packages > tasks.
 * Uses the same phases endpoint as the tree view, which already delivers
 * the work packages of every phase.
 */
const ProjectTaskView = ({ project }) => {
  const { t } = useTranslation();

  // this response carries the taskStats the overview shows
  const { apiData, setApiData } = useFetch(`projects/${project.id}/phases`);

  // a changed task only moves a few counters, so they are written into the
  // loaded phases instead of fetching them again
  const handleStatsChange = useCallback(
    (workPackageId, delta) =>
      setApiData((prev) =>
        prev
          ? {
              ...prev,
              data: applyTaskStatsDelta(prev.data || [], workPackageId, delta),
            }
          : prev,
      ),
    [],
  );

  // one filter for the whole view, applied to every open task list. The
  // settings survive a reload, only the search starts out empty again.
  const [storedFilter, setStoredFilter] = usePersistedState(
    'projectTasks.filter',
    emptyTaskFilter,
  );
  const [search, setSearch] = useState('');

  const filter = { ...storedFilter, search };

  const setFilter = useCallback(
    (next) => {
      const { search: nextSearch, ...rest } = next;
      setSearch(nextSearch || '');
      setStoredFilter((prev) => ({ ...prev, ...rest }));
    },
    [setStoredFilter],
  );

  // every open list reports while it reloads, so a filter change gets a single
  // processing indicator instead of one per work package
  const [loadingIds, setLoadingIds] = useState([]);

  const handleListLoading = useCallback(
    (workPackageId, isLoading) =>
      setLoadingIds((prev) => {
        if (isLoading)
          return prev.includes(workPackageId) ? prev : [...prev, workPackageId];
        return prev.filter((id) => id !== workPackageId);
      }),
    [],
  );

  const teamMembers = project.supervisor
    ? [
        project.supervisor,
        ...(project.teamMembers || []).filter(
          (user) => user.id !== project.supervisor.id,
        ),
      ]
    : project.teamMembers || [];

  if (!apiData) return <ExoSkeleton n={3} size="70px" />;

  const phases = apiData.data || [];

  if (!phases.length)
    return (
      <Typography className="opacity-50 py-4 text-center">
        {t('No data available')}
      </Typography>
    );

  // hideEmpty keeps phases without a listable work package off the screen -
  // the overview above still counts everything
  const shownPhases = visiblePhases(phases, filter);

  return (
    <Box className="flex flex-col gap-3 pt-4">
      <ProjectOverviewStats phases={phases} />

      <TaskFilterBar users={teamMembers} filter={filter} onChange={setFilter} />

      {shownPhases.map((phase, index) => (
        <ProjectTaskPhase
          key={phase.id}
          project={project}
          phase={phase}
          filter={filter}
          onLoadingChange={handleListLoading}
          onStatsChange={handleStatsChange}
          defaultExpanded={index === 0}
        />
      ))}

      {!shownPhases.length && (
        <Typography fontSize="13px" className="opacity-50 py-4 text-center">
          {t('All phases are hidden by the filters')}
        </Typography>
      )}

      {/* the phases render inside backdrop-blurred boxes, which would pin the
          fixed snackbar to them instead of the viewport */}
      {createPortal(
        <ExoProcessing open={loadingIds.length > 0} label={t('Loading')} />,
        document.body,
      )}
    </Box>
  );
};

export default ProjectTaskView;
